import type { LedgerAccountType } from '@fragment/types';
import type { CurrencyMode } from '@fragment/types/currencyCodes';
import type { ConsistencyMode } from '@fragment/types/schemas';

/** One account's own inheritance-relevant fields, exactly as declared. */
export type OwnAccountFields<Currency> = {
  type: LedgerAccountType | undefined;
  currency: Currency | undefined;
  currencyMode: CurrencyMode | undefined;
  ownBalanceUpdates: ConsistencyMode | undefined;
  totalBalanceUpdates?: ConsistencyMode;
};

/**
 * The already-resolved fields an account inherits from: its parent's resolved
 * fields, or the schema defaults at the root.
 */
export type InheritedAccountFields<Currency> = {
  type: LedgerAccountType | undefined;
  currency: Currency | undefined;
  currencyMode: CurrencyMode;
  ownBalanceUpdates?: ConsistencyMode;
};

export type ResolvedAccountFields<Currency> =
  InheritedAccountFields<Currency> & {
    totalBalanceUpdates?: ConsistencyMode;
  };

/**
 * Resolves the fields on a single account that are inherited from its ancestors.
 *
 */
export const resolveAccountInheritedFields = <Currency>({
  own,
  inherited,
  isTotalBalance,
}: {
  own: OwnAccountFields<Currency>;
  inherited: InheritedAccountFields<Currency>;
  isTotalBalance: boolean;
}): ResolvedAccountFields<Currency> => {
  const currency =
    !own.currency && (own.currencyMode ?? inherited.currencyMode) === 'multi'
      ? undefined
      : own.currency ?? inherited.currency;
  const resolveBalanceUpdates = (): Pick<
    ResolvedAccountFields<Currency>,
    'ownBalanceUpdates' | 'totalBalanceUpdates'
  > => {
    if (!isTotalBalance) {
      return {
        ownBalanceUpdates: own.ownBalanceUpdates ?? inherited.ownBalanceUpdates,
      };
    }
    return own.totalBalanceUpdates !== undefined
      ? { totalBalanceUpdates: own.totalBalanceUpdates }
      : {};
  };
  return {
    type: own.type ?? inherited.type,
    currency,
    // An account with a currency is necessarily single-currency, unless it
    // overrides currencyMode itself.
    currencyMode:
      own.currencyMode ?? (currency ? 'single' : inherited.currencyMode),
    ...resolveBalanceUpdates(),
  };
};

/**
 * Solves a bug where name was set to the first part of the key.
 * In that case, use the full key instead of the name.
 */
export const inferAccountName = ({
  key,
  name,
}: {
  key: string;
  name: string | undefined;
}): string => (name && !key.startsWith(name) ? name : key);
